import { InvoiceStatus, StripePaymentStatus } from './invoice';

// "Open AR" = finalized, non-void invoices with status New or Entered (items 44 + 67 + 118).
// Per item 116's status model: New = finalized+sent, accountant queue; NewEntered = reviewed,
// payment not yet confirmed. PaidEntered = confirmed received (goes to Paid KPI, not AR).
// item 579 — a Stripe-paid invoice (stripePaymentStatus set) also LEAVES open AR: the money
// is received, it only awaits the accountant's deposit confirm (Deposits ledger, not AR /
// collections / statements). KPI running totals are untouched — they don't use this selector.
// Dashboard Outstanding and the AR report share this ONE selector — they must stay in sync.
type ArShape = {
  isVoid?: boolean;
  isFinalized?: boolean;
  status: InvoiceStatus;
  stripePaymentStatus?: StripePaymentStatus;
  // item 1038 — a manually-recorded payment arrival (Mark paid: check/wire in hand)
  // leaves open AR exactly like a Stripe payment; the deposit confirm comes later
  paidDate?: string | null;
};

export const isOpenAr = (i: ArShape): boolean => !i.isVoid
  && i.isFinalized === true
  && (i.status === InvoiceStatus.New || i.status === InvoiceStatus.NewEntered)
  && (i.stripePaymentStatus ?? StripePaymentStatus.None) === StripePaymentStatus.None
  && !i.paidDate;

export const selectOpenAr = <T extends ArShape>(
  invoices: T[],
): T[] => invoices.filter(isOpenAr);

// item 1127 (Chad: "our AR report will always be accurate") — UNPAID subscription charges are
// open AR too, mirroring the invoice rule: no Stripe payment, no manual payment recorded, not
// deposited. Paid-awaiting-deposit leaves AR exactly like invoices do. One selector + one
// invoice-shaped adapter so every AR surface (Outstanding KPI, AR report, Pipeline,
// Collections) merges charges the same way.
type ChargeArShape = {
  stripePaid?: boolean;
  paidDate?: string | null;
  isDeposited?: boolean;
};

export const isOpenArCharge = (c: ChargeArShape): boolean => !c.stripePaid && !c.paidDate && !c.isDeposited;

export interface SubscriptionArRow {
  id: string;
  invoiceNumber: string;
  customerId?: string | null;
  customerName: string;
  projectName: string;
  date: string;
  retailTotal: number;
  isSubscriptionCharge: true;
  lines: string[]; // item 1128 — the charge's snapshotted line breakdown, for hovers
  memo?: string | null; // item 1130 — the Stripe invoice memo (client/agreement info)
}

type ChargeRowShape = ChargeArShape & {
  id: string;
  stripeInvoiceNumber?: string | null;
  customerId?: string | null;
  customerName?: string | null;
  recurringName?: string | null;
  projectName?: string | null;
  amount: number;
  issuedDate: string;
  linesJson?: string | null;
  memoText?: string | null;
};

// item 1128 — human lines from a charge's LinesJson snapshot ([{sku,brand,description,amount}])
export const chargeLines = (linesJson?: string | null): string[] => {
  if (!linesJson) return [];
  try {
    const parsed = JSON.parse(linesJson) as { description?: string; sku?: string; amount?: number }[];
    if (!Array.isArray(parsed)) return [];
    return parsed.map((l) => {
      const name = l.description || l.sku || 'Subscription line';
      const amt = typeof l.amount === 'number'
        ? ` — $${l.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : '';
      return `${name}${amt}`;
    });
  } catch { return []; }
};

export const selectOpenArCharges = <T extends ChargeRowShape>(charges: T[]): SubscriptionArRow[] => charges
  .filter(isOpenArCharge)
  .map((c) => ({
    id: c.id,
    invoiceNumber: c.stripeInvoiceNumber || 'Subscription',
    customerId: c.customerId,
    customerName: c.customerName ?? c.recurringName ?? '—',
    projectName: c.projectName ?? '',
    date: c.issuedDate,
    retailTotal: c.amount,
    isSubscriptionCharge: true as const,
    lines: chargeLines(c.linesJson),
    memo: c.memoText,
  }));

// whole days since the invoice date (aging); a future/blank date clamps to 0
export const invoiceAgeDays = (dateStr?: string, now: Date = new Date()): number => {
  if (!dateStr) return 0;
  const d = new Date(`${dateStr.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return 0;
  return Math.max(0, Math.floor((now.getTime() - d.getTime()) / 86_400_000));
};

// item 1163e — date-only arithmetic that never round-trips the API string through a
// timezone (same discipline as SqlTools): returns yyyy-MM-dd, or '' on a blank/bad input.
export const addDaysToSqlDate = (dateStr: string | undefined, days: number): string => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  const t = new Date(Date.UTC(y, m - 1, d + days));
  if (Number.isNaN(t.getTime())) return '';
  const mm = `${t.getUTCMonth() + 1}`.padStart(2, '0');
  const dd = `${t.getUTCDate()}`.padStart(2, '0');
  return `${t.getUTCFullYear()}-${mm}-${dd}`;
};

export const AGING_BUCKETS = ['0–30', '31–60', '61–90', '90+'] as const;
export type AgingBucket = typeof AGING_BUCKETS[number];

export const agingBucket = (days: number): AgingBucket => {
  if (days <= 30) return '0–30';
  if (days <= 60) return '31–60';
  if (days <= 90) return '61–90';
  return '90+';
};
